import { z } from "zod";

/** Generation/attribution provenance carried on a block (opaque to the kernel). */
export const blockProvenanceHintSchema = z.record(z.string(), z.unknown());

/** Settings every kind accepts until per-kind settings land (P4 motion, P14 renderers). */
export const emptyBlockSettingsSchema = z.object({}).strict();

/** Canonical EN instruction lines spoken before interactive kinds (narration + duration fallback). */
export const TOPIC_BLOCK_INSTRUCTION_TEMPLATES = {
	IMAGE: "Click to open the image to explore.",
	VIDEO: "Click to play the video.",
	QUIZ: "Choose the correct answer",
	POLL: "Cast your vote.",
	REVEAL: "Tap to reveal.",
	REFLECTION: "Take a moment to reflect.",
} as const;

export type TopicBlockInstructionKind =
	keyof typeof TOPIC_BLOCK_INSTRUCTION_TEMPLATES;

export function getTopicBlockInstructionTemplate(kind: string): string | null {
	const key = kind.toUpperCase();
	const templates: Readonly<Record<string, string>> =
		TOPIC_BLOCK_INSTRUCTION_TEMPLATES;
	return templates[key] ?? null;
}

export function asRecord(value: unknown): Record<string, unknown> {
	return value != null && typeof value === "object" && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: {};
}

/** Trimmed string (numbers stringified); "" otherwise. */
export function str(value: unknown): string {
	if (typeof value === "string") {
		return value.trim();
	}
	if (typeof value === "number" && Number.isFinite(value)) {
		return String(value);
	}
	return "";
}

/** Optional trimmed string: present only when non-empty. */
export function optionalStr(value: unknown): string | undefined {
	const text = str(value);
	return text.length > 0 ? text : undefined;
}

export function joinParts(parts: readonly string[]): string {
	return parts
		.map((part) => part.trim())
		.filter((part) => part.length > 0)
		.join("\n\n");
}

/** Keeps `provenanceHint` when it is an object. */
export function pickProvenance(raw: Record<string, unknown>): {
	provenanceHint?: Record<string, unknown>;
} {
	const hint = raw.provenanceHint;
	return hint != null && typeof hint === "object" && !Array.isArray(hint)
		? { provenanceHint: hint as Record<string, unknown> }
		: {};
}

/** Drops keys whose value is `undefined` (canonical content never stores them). */
export function compact<T extends Record<string, unknown>>(value: T): T {
	return Object.fromEntries(
		Object.entries(value).filter(([, entry]) => entry !== undefined),
	) as T;
}
